import { useEffect, useRef } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'

/**
 * Payload of the API's "WebsterBotStatusChanged" SignalR event, broadcast to the
 * workspace-{workspaceId} group whenever a meeting's Webster bot advances (WebsterBotStatusService
 * on the webhooks, WebsterBotSyncService on scheduling). camelCase to match the wire event; the
 * dashboard maps it onto the touchpoint's PascalCase DTO. `hasRecording` is present, but the
 * transcript URL is not, so consumers reconcile that with a refetch on completion.
 */
export interface WebsterBotStatusEvent {
  touchPointId: string
  workspaceId: string
  campaignId?: string | null
  webId?: string | null
  botStatus: string
  botStatusSubCode?: string | null
  hasRecording: boolean
}

/**
 * Subscribes to live Webster bot-status changes for a workspace (joins workspace-{workspaceId}). The
 * meeting list uses it to flip a badge Scheduled -> In call -> Recorded without a refresh. The
 * handler is held in a ref so re-rendering with a new closure never re-subscribes; subscribe
 * failures are swallowed (the list still works, just without live updates).
 */
export function useMeetingBotStatus(
  workspaceId: string | undefined,
  handler: (evt: WebsterBotStatusEvent) => void,
): void {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!workspaceId) return
    const group = `workspace-${workspaceId}`
    let cancelled = false

    const onEvent = (payload: unknown) => handlerRef.current(payload as WebsterBotStatusEvent)

    signalRService.joinGroup(group).then(() => {
      if (cancelled) {
        signalRService.leaveGroup(group)
        return
      }
      signalRService.on('WebsterBotStatusChanged', onEvent)
    }).catch(() => { /* non-fatal: no live updates, the list still loads and refreshes normally */ })

    return () => {
      cancelled = true
      signalRService.off('WebsterBotStatusChanged', onEvent)
      signalRService.leaveGroup(group)
    }
  }, [workspaceId])
}
